import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { Box, TextField, Theme, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const useStyles = makeStyles()((theme: Theme) => ({
  defaultFileField: {
    visibility: 'hidden',
    width: 0,
    height: 0,
    input: {
      visibility: 'hidden',
    },
  },
  fieldLabel: {
    boxSizing: 'border-box',
    display: 'inline-flex',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: theme.palette.info.dark,
    borderRadius: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.common.white,

    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.info.main,
    },
  },
  fieldLabelDisabled: {
    color: theme.palette.text.disabled,
    background: 'rgba(255, 255, 255, 0.08)',

    '&:hover': {
      cursor: 'default',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  fileName: {
    marginTop: '8px',
    fontSize: '14px',
    color: theme.palette.common.white,
    fontStyle: 'italic',
  },
  existingFileContainer: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#333d4667',
    border: '1px solid #c9c2c271',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  existingFileName: {
    fontSize: '14px',
    color: '#ffffff',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    maxWidth: '100%',
  },
  fileNameText: {
    wordBreak: 'break-all',
    whiteSpace: 'normal',
    lineHeight: 1.4,
    flex: 1,
    minWidth: 0,
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  iconButton: {
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  supportedFormats: {
    marginTop: '4px',
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
  },
  errorText: {
    marginTop: '4px',
    fontSize: '12px',
    color: theme.palette.error.main,
    fontFamily: 'Inter',
    fontWeight: 500,
  },
}));

interface FormPDFUploadFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  onFileSelect?: (file: File) => void;
  existingFileName?: string;
  onDownload?: () => void;
  onDelete?: () => void;
  maxWidth?: string | number;
  minWidth?: string | number;
  // Overrides for the accepted file types and size limit
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
  maxSizeMB?: number;
  formatsLabel?: string;
}

const DEFAULT_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/tiff'
];

const DEFAULT_ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.tiff'];

const FormPDFUploadField = ({
  name,
  label,
  disabled,
  onFileSelect,
  existingFileName,
  onDownload,
  onDelete,
  maxWidth,
  minWidth,
  allowedMimeTypes = DEFAULT_ALLOWED_MIME_TYPES,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  maxSizeMB = 2,
  formatsLabel = 'PDF, DOC, DOCX, JPG, JPEG, or TIFF',
}: FormPDFUploadFieldProps) => {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const { classes } = useStyles();
  const [fileKey, setFileKey] = useState(Date.now());


  const validateFile = (file: File): boolean => {
    // File size validation
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      setError(name, {
        type: 'manual',
        message: `File size is ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum allowed size is ${maxSizeMB}MB.`
      });
      return false;
    }

    // File type validation
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedMimeTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setError(name, {
        type: 'manual',
        message: `File format "${fileExtension.toUpperCase() || 'unknown'}" is not supported. Please use: ${formatsLabel}.`
      });
      return false;
    }

    clearErrors(name);
    return true;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { value, onChange } = field;
        useEffect(() => {
          if (!value) {
            setFileKey(Date.now());
          }
        }, [value]);
        return (
          <Box>
            {/* Display existing file if available */}
            {existingFileName && !value && (
              <Box className={classes.existingFileContainer}>
                <span className={classes.existingFileName}>
                  <InsertDriveFileIcon sx={{ color: (theme) => theme.palette.info.main }} />
                  <span className={classes.fileNameText}>{existingFileName}</span>
                </span>
                <Box className={classes.actionButtons}>
                  {onDownload && (
                    <Tooltip title="Download file">
                      <IconButton
                        size="small"
                        onClick={onDownload}
                        className={classes.iconButton}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="Delete file">
                      <IconButton
                        size="small"
                        onClick={onDelete}
                        className={classes.iconButton}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            {value && (
              <Box className={classes.existingFileContainer} mt="12px">
                <span className={classes.existingFileName}>
                  <InsertDriveFileIcon sx={{ color: (theme) => theme.palette.info.main }} />
                  <span className={classes.fileNameText}>{value.name}</span>
                </span>

                <Box className={classes.actionButtons}>
                  {/* Download newly selected file */}
                  <Tooltip title="Download file">
                    <IconButton
                      size="small"
                      className={classes.iconButton}
                      onClick={() => {
                        const url = URL.createObjectURL(value);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = value.name;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Delete newly selected file */}
                  <Tooltip title="Delete file">
                    <IconButton
                      size="small"
                      className={classes.iconButton}
                      onClick={() => {
                        onChange(null);
                        setFileKey(Date.now());  // <-- important fix
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onFileSelect?.(null as any);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}

            <Box mt="16px">
              <label className={clsx(classes.fieldLabel, disabled && classes.fieldLabelDisabled)} style={{ maxWidth: maxWidth || '200px', minWidth: minWidth || undefined }}>
                <TextField
                  key={fileKey}
                  className={classes.defaultFileField}
                  name={name}
                  type="file"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files?.length) {
                      const file = e.target.files[0];
                      if (validateFile(file)) {
                        onChange(file);
                        onFileSelect && onFileSelect(file);
                      } else {
                        e.target.value = '';
                      }
                    } else {
                      clearErrors(name);
                      onChange(null);
                    }
                  }}
                  inputProps={{
                    accept: allowedExtensions.join(','),
                  }}
                  disabled={disabled}
                />
                {label}
              </label>

              {/* Error message display - simple text like supported formats */}
              {errors[name] && (
                <div className={classes.errorText}>
                  {String(errors[name]?.message || 'Invalid file')}
                </div>
              )}

              {/* Supported formats info */}
              {/* {!value && !errors[name] && !existingFileName && ( */}
              <div className={classes.supportedFormats}>
                Supported formats: {formatsLabel} (Max: {maxSizeMB}MB)
              </div>
              {/* )} */}

              {value && (
                <div className={classes.fileName}>
                  Selected: {value.name} ({
                    value.size < 1000 * 1024
                      ? `${(value.size / 1024).toFixed(2)} KB`
                      : `${(value.size / 1024 / 1024).toFixed(2)} MB`
                  })
                </div>
              )}
            </Box>
          </Box>
        )
      }}
    />
  );
};

export default FormPDFUploadField;